// Localização: ./src/pages/User/ProfilePage.jsx
// Página de Perfil do Usuário - Versão 2.0.0 (Refatorada - Sem ícones, com upload de foto)

import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { getMyProfile, updateMyProfile, uploadAssinatura, createOrganization, getVinculosPendentes, aceitarVinculo, rejeitarVinculo } from '../../api/api';
import Card from '../../components/UI/Card';
import CardContent from '../../components/UI/CardContent';
import LoadingIndicator from '../../components/Common/LoadingIndicator';
import Alert from '../../components/UI/Alert';
import Title from '../../components/Layout/Title';
import { Camera, CheckCircle2, XCircle, AlertCircle, FileText, Upload } from 'lucide-react';
import { getUsuarioFotoUrl } from '../../utils/imageUtils';

const ProfilePage = () => {
    const { user: authUser, setUser } = useAuth();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [profileData, setProfileData] = useState(null);
    
    // Estados para edição
    const [isEditingPersonal, setIsEditingPersonal] = useState(false);
    const [isEditingPassword, setIsEditingPassword] = useState(false);
    const [isCreatingOrganization, setIsCreatingOrganization] = useState(false);
    const [isEditingPhoto, setIsEditingPhoto] = useState(false);
    
    // Estados para foto
    const [fotoFile, setFotoFile] = useState(null);
    const [fotoPreview, setFotoPreview] = useState(null);
    const fileInputRef = useRef(null);
    
    // Estados para assinatura
    const [isEditingAssinatura, setIsEditingAssinatura] = useState(false);
    const [assinaturaFile, setAssinaturaFile] = useState(null);
    const assinaturaInputRef = useRef(null);
    
    // Formulário de dados pessoais
    const [nomeCompleto, setNomeCompleto] = useState('');
    const [email, setEmail] = useState('');
    const [telefone, setTelefone] = useState('');
    
    // Formulário de senha
    const [senhaAtual, setSenhaAtual] = useState('');
    const [novaSenha, setNovaSenha] = useState('');
    const [confirmarSenha, setConfirmarSenha] = useState('');
    
    // Formulário de organização
    const [nomeOrganizacao, setNomeOrganizacao] = useState('');
    const [cnpj, setCnpj] = useState('');

    // Estados para vínculos pendentes
    const [vinculosPendentes, setVinculosPendentes] = useState([]);
    const [loadingVinculos, setLoadingVinculos] = useState(false);
    const [processandoVinculo, setProcessandoVinculo] = useState(null);

    // Carregar dados do perfil
    useEffect(() => {
        loadProfile();
        loadVinculosPendentes();
    }, []);

    const loadProfile = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await getMyProfile();
            const data = response.data;
            setProfileData(data);
            setNomeCompleto(data.nome_completo || '');
            setEmail(data.email || '');
            setTelefone(data.telefone || '');
            
            // Carregar preview da foto se existir
            if (data.fotoURL) {
                setFotoPreview(getUsuarioFotoUrl(data.fotoURL));
            } else {
                setFotoPreview(null);
            }
            
            // Assinatura não precisa de preview, apenas verificar se existe
        } catch (err) {
            console.error('[ProfilePage] Erro ao carregar perfil:', err);
            setError(err.response?.data?.message || 'Erro ao carregar dados do perfil.');
        } finally {
            setLoading(false);
        }
    };

    // Carregar vínculos pendentes
    const loadVinculosPendentes = async () => {
        try {
            setLoadingVinculos(true);
            const response = await getVinculosPendentes();
            setVinculosPendentes(response.data.vinculos || []);
        } catch (err) {
            console.error('[ProfilePage] Erro ao carregar vínculos pendentes:', err);
            // Não mostrar erro se não houver vínculos pendentes
        } finally {
            setLoadingVinculos(false);
        }
    };

    // Aceitar vínculo
    const handleAceitarVinculo = async (organizacaoId) => {
        try {
            setProcessandoVinculo(organizacaoId);
            setError(null);
            await aceitarVinculo({ organizacao_id: organizacaoId });
            setSuccess('Vínculo aceito com sucesso!');
            // Recarregar dados
            await loadProfile();
            await loadVinculosPendentes();
            // Atualizar dados do usuário no contexto
            if (authUser && setUser) {
                const profileResponse = await getMyProfile();
                setUser(profileResponse.data);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Erro ao aceitar vínculo.');
        } finally {
            setProcessandoVinculo(null);
        }
    };

    // Rejeitar vínculo
    const handleRejeitarVinculo = async (organizacaoId) => {
        if (!window.confirm('Tem certeza que deseja rejeitar este vínculo?')) {
            return;
        }

        try {
            setProcessandoVinculo(organizacaoId);
            setError(null);
            await rejeitarVinculo({ organizacao_id: organizacaoId });
            setSuccess('Vínculo rejeitado.');
            // Recarregar dados
            await loadProfile();
            await loadVinculosPendentes();
        } catch (err) {
            setError(err.response?.data?.message || 'Erro ao rejeitar vínculo.');
        } finally {
            setProcessandoVinculo(null);
        }
    };

    // Handler para mudança de arquivo de foto
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validar tipo de arquivo
            if (!file.type.startsWith('image/')) {
                setError('Por favor, selecione um arquivo de imagem.');
                return;
            }
            
            // Validar tamanho (máximo 5MB)
            if (file.size > 5 * 1024 * 1024) {
                setError('A imagem deve ter no máximo 5MB.');
                return;
            }
            
            setFotoFile(file);
            setFotoPreview(URL.createObjectURL(file));
            setIsEditingPhoto(true);
        }
    };

    // Handler para salvar foto
    const handleSavePhoto = async () => {
        if (!fotoFile) {
            setError('Por favor, selecione uma foto.');
            return;
        }

        setError(null);
        setSuccess(null);
        setSaving(true);

        try {
            const formData = new FormData();
            formData.append('foto', fotoFile);

            const response = await updateMyProfile(formData);
            
            // Recarregar perfil para obter a nova URL da foto
            await loadProfile();
            
            // Atualizar contexto de autenticação
            if (response.data?.usuario && setUser) {
                setUser(prevUser => ({
                    ...prevUser,
                    fotoURL: response.data.usuario.fotoURL,
                }));
            }

            setSuccess('Foto atualizada com sucesso!');
            setIsEditingPhoto(false);
            setFotoFile(null);
        } catch (err) {
            console.error('Erro ao atualizar foto:', err);
            setError(err.response?.data?.message || 'Erro ao atualizar foto.');
        } finally {
            setSaving(false);
        }
    };

    // Cancelar edição de foto
    const cancelEditPhoto = () => {
        setFotoFile(null);
        if (profileData?.fotoURL) {
            setFotoPreview(getUsuarioFotoUrl(profileData.fotoURL));
        } else {
            setFotoPreview(null);
        }
        setIsEditingPhoto(false);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // Handler para mudança de arquivo de assinatura
    const handleAssinaturaFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validar tipo de arquivo (apenas PNG)
            if (file.type !== 'image/png') {
                setError('Por favor, selecione um arquivo PNG.');
                return;
            }
            
            // Validar tamanho (máximo 2MB)
            if (file.size > 2 * 1024 * 1024) {
                setError('O arquivo PNG deve ter no máximo 2MB.');
                return;
            }
            
            setAssinaturaFile(file);
            setIsEditingAssinatura(true);
        }
    };

    // Handler para salvar assinatura
    const handleSaveAssinatura = async () => {
        if (!assinaturaFile) {
            setError('Por favor, selecione um arquivo PNG.');
            return;
        }

        setError(null);
        setSuccess(null);
        setSaving(true);

        try {
            const formData = new FormData();
            formData.append('assinatura', assinaturaFile);

            const response = await uploadAssinatura(formData);
            
            // Recarregar perfil para obter a nova URL da assinatura
            await loadProfile();
            
            // Atualizar contexto de autenticação
            if (response.data?.usuario && setUser) {
                setUser(prevUser => ({
                    ...prevUser,
                    assinaturaURL: response.data.usuario.assinaturaURL,
                }));
            }

            setSuccess('Assinatura atualizada com sucesso!');
            setIsEditingAssinatura(false);
            setAssinaturaFile(null);
        } catch (err) {
            console.error('Erro ao atualizar assinatura:', err);
            setError(err.response?.data?.message || 'Erro ao atualizar assinatura.');
        } finally {
            setSaving(false);
        }
    };

    // Cancelar edição de assinatura
    const cancelEditAssinatura = () => {
        setAssinaturaFile(null);
        setIsEditingAssinatura(false);
        if (assinaturaInputRef.current) {
            assinaturaInputRef.current.value = '';
        }
    };

    const handleSavePersonal = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);
        setSaving(true);

        try {
            const updateData = {};
            if (nomeCompleto !== profileData.nome_completo) {
                updateData.nome_completo = nomeCompleto;
            }
            if (email !== profileData.email) {
                updateData.email = email;
            }
            if (telefone !== profileData.telefone) {
                updateData.telefone = telefone;
            }

            if (Object.keys(updateData).length === 0) {
                setError('Nenhuma alteração foi feita.');
                setSaving(false);
                return;
            }

            const response = await updateMyProfile(updateData);
            
            await loadProfile();
            
            if (response.data?.usuario && setUser) {
                setUser(prevUser => ({
                    ...prevUser,
                    nome: response.data.usuario.nome_completo,
                    email: response.data.usuario.email,
                }));
            }

            setSuccess('Dados pessoais atualizados com sucesso!');
            setIsEditingPersonal(false);
        } catch (err) {
            console.error('Erro ao atualizar perfil:', err);
            setError(err.response?.data?.message || 'Erro ao atualizar dados pessoais.');
        } finally {
            setSaving(false);
        }
    };

    const handleSavePassword = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);

        if (!senhaAtual || !novaSenha || !confirmarSenha) {
            setError('Por favor, preencha todos os campos de senha.');
            return;
        }

        if (novaSenha.length < 8) {
            setError('A nova senha deve ter no mínimo 8 caracteres.');
            return;
        }

        if (novaSenha !== confirmarSenha) {
            setError('As senhas não coincidem.');
            return;
        }

        setSaving(true);

        try {
            await updateMyProfile({
                senha: novaSenha,
                senha_atual: senhaAtual,
            });

            setSuccess('Senha alterada com sucesso!');
            setIsEditingPassword(false);
            setSenhaAtual('');
            setNovaSenha('');
            setConfirmarSenha('');
        } catch (err) {
            console.error('Erro ao alterar senha:', err);
            setError(err.response?.data?.message || 'Erro ao alterar senha.');
        } finally {
            setSaving(false);
        }
    };

    const cancelEditPersonal = () => {
        setNomeCompleto(profileData?.nome_completo || '');
        setEmail(profileData?.email || '');
        setTelefone(profileData?.telefone || '');
        setIsEditingPersonal(false);
        setError(null);
    };

    const cancelEditPassword = () => {
        setSenhaAtual('');
        setNovaSenha('');
        setConfirmarSenha('');
        setIsEditingPassword(false);
        setError(null);
    };

    const handleCreateOrganization = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);
        setSaving(true);

        if (!nomeOrganizacao.trim()) {
            setError('Nome da organização é obrigatório.');
            setSaving(false);
            return;
        }

        if (!cnpj.trim()) {
            setError('CNPJ é obrigatório.');
            setSaving(false);
            return;
        }

        const cnpjLimpo = cnpj.replace(/\D/g, '');

        if (cnpjLimpo.length !== 14) {
            setError('CNPJ deve conter 14 dígitos.');
            setSaving(false);
            return;
        }

        try {
            const response = await createOrganization({
                nome_organizacao: nomeOrganizacao.trim(),
                cnpj: cnpjLimpo
            });

            setSuccess('Organização criada com sucesso! Você agora é o proprietário. Recarregando...');
            
            await loadProfile();
            
            if (response.data?.usuario && setUser) {
                setUser(prevUser => ({
                    ...prevUser,
                    organizacao_id: response.data.organizacao.id,
                    role: 'proprietario',
                    status: 'ativo'
                }));
            }

            setNomeOrganizacao('');
            setCnpj('');
            setIsCreatingOrganization(false);

            setTimeout(() => {
                window.location.reload();
            }, 2000);

        } catch (err) {
            console.error('Erro ao criar organização:', err);
            setError(err.response?.data?.message || 'Erro ao criar organização.');
        } finally {
            setSaving(false);
        }
    };

    const cancelCreateOrganization = () => {
        setNomeOrganizacao('');
        setCnpj('');
        setIsCreatingOrganization(false);
        setError(null);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Não informado';
        const date = new Date(dateString);
        return date.toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        });
    };

    const getRoleLabel = (role) => {
        const roles = {
            'proprietario': 'Proprietário',
            'admin': 'Administrador',
            'gerente': 'Gerente',
            'cuidador': 'Cuidador',
            'enfermeiro': 'Enfermeiro',
            'medico': 'Médico',
            'fisioterapeuta': 'Fisioterapeuta',
        };
        return roles[role] || role;
    };

    const getStatusLabel = (status) => {
        const statuses = {
            'ativo': 'Ativo',
            'pendente_vinculacao': 'Pendente de Vinculação',
            'pendente_aceitacao': 'Pendente de Aceitação',
            'inativo': 'Inativo',
        };
        return statuses[status] || status;
    };

    // Obter iniciais para avatar padrão
    const getIniciais = () => {
        if (profileData?.nome_completo) {
            const partes = profileData.nome_completo.trim().split(' ');
            if (partes.length >= 2) {
                return `${partes[0][0]}${partes[partes.length - 1][0]}`.toUpperCase();
            }
            return partes[0][0].toUpperCase();
        }
        return 'U';
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Carregando perfil...</p>
                </div>
            </div>
        );
    }

    if (!profileData) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Alert type="error">Não foi possível carregar os dados do perfil.</Alert>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto space-y-6 px-4 sm:px-6 lg:px-8 py-6">
            <Title>Meu Perfil</Title>

            {/* Mensagens de Erro e Sucesso */}
            {error && (
                <Alert type="error" onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}
            {success && (
                <Alert type="success" onClose={() => setSuccess(null)}>
                    {success}
                </Alert>
            )}

            {/* Card de Foto do Perfil */}
            <Card>
                <CardContent className="p-6 sm:p-8">
                    <div className="flex flex-col items-center space-y-6">
                        {/* Avatar/Foto */}
                        <div className="relative">
                            <div className="h-32 w-32 rounded-full overflow-hidden bg-gray-100 flex items-center justify-center border-4 border-gray-200">
                                {fotoPreview ? (
                                    <img 
                                        src={fotoPreview} 
                                        alt="Foto do perfil" 
                                        className="h-full w-full object-cover"
                                    />
                                ) : (
                                    <div className="h-full w-full bg-higeia-dark flex items-center justify-center">
                                        <span className="text-4xl font-bold text-white">
                                            {getIniciais()}
                                        </span>
                                    </div>
                                )}
                            </div>
                            
                            {/* Botão de editar foto */}
                            {!isEditingPhoto ? (
                                <button
                                    onClick={() => fileInputRef.current?.click()}
                                    className="absolute bottom-0 right-0 bg-higeia-dark text-white p-2 rounded-full shadow-lg hover:bg-higeia-dark/90 transition-colors"
                                    title="Alterar foto"
                                >
                                    <Camera className="h-5 w-5" />
                                </button>
                            ) : (
                                <div className="absolute bottom-0 right-0 flex gap-2">
                                    <button
                                        onClick={handleSavePhoto}
                                        disabled={saving}
                                        className="bg-emerald-600 text-white p-2 rounded-full shadow-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
                                        title="Salvar foto"
                                    >
                                        {saving ? (
                                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                        ) : (
                                            <span className="text-sm font-semibold">✓</span>
                                        )}
                                    </button>
                                    <button
                                        onClick={cancelEditPhoto}
                                        className="bg-gray-500 text-white p-2 rounded-full shadow-lg hover:bg-gray-600 transition-colors"
                                        title="Cancelar"
                                    >
                                        <span className="text-sm font-semibold">×</span>
                                    </button>
                                </div>
                            )}
                            
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                onChange={handleFileChange}
                                className="hidden"
                            />
                        </div>

                        {/* Nome do usuário */}
                        <div className="text-center px-4">
                            <h2 className="text-2xl font-bold text-gray-800">
                                {profileData.nome_completo || 'Usuário'}
                            </h2>
                            <p className="text-gray-500 mt-2">{getRoleLabel(profileData.role)}</p>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Card de Assinatura Digital */}
            <Card>
                <CardContent className="p-6 sm:p-8">
                    <div className="flex items-center justify-between mb-8">
                        <div>
                            <h2 className="text-2xl font-bold text-gray-800">
                                Assinatura Digital
                            </h2>
                            <p className="text-sm text-gray-500 mt-1">
                                Faça upload de um arquivo PNG com sua assinatura para usar em notas clínicas
                            </p>
                        </div>
                        {!isEditingAssinatura && (
                            <button
                                onClick={() => {
                                    setIsEditingAssinatura(true);
                                    // Pequeno delay para garantir que o input seja renderizado
                                    setTimeout(() => {
                                        assinaturaInputRef.current?.click();
                                    }, 100);
                                }}
                                className="px-4 py-2 text-sm font-medium text-emerald-600 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-colors flex items-center gap-2"
                            >
                                <Upload className="h-4 w-4" />
                                {profileData?.assinaturaURL ? 'Alterar' : 'Adicionar'}
                            </button>
                        )}
                        {isEditingAssinatura && (
                            <button
                                onClick={cancelEditAssinatura}
                                className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                Cancelar
                            </button>
                        )}
                    </div>

                    {!isEditingAssinatura ? (
                        <div className="space-y-4">
                            {profileData?.assinaturaURL ? (
                                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-center gap-3">
                                    <FileText className="h-8 w-8 text-emerald-600" />
                                    <div className="flex-1">
                                        <p className="text-sm font-medium text-emerald-800">
                                            Assinatura cadastrada
                                        </p>
                                        <p className="text-xs text-emerald-600 mt-1">
                                            Sua assinatura será usada automaticamente ao assinar notas clínicas
                                        </p>
                                    </div>
                                    <CheckCircle2 className="h-6 w-6 text-emerald-600" />
                                </div>
                            ) : (
                                <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
                                    <FileText className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                                    <p className="text-sm text-gray-600">
                                        Nenhuma assinatura cadastrada
                                    </p>
                                    <p className="text-xs text-gray-500 mt-1">
                                        Faça upload de um arquivo PNG com sua assinatura
                                    </p>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Arquivo PDF da Assinatura *
                                </label>
                                <input
                                    ref={assinaturaInputRef}
                                    type="file"
                                    accept="image/png"
                                    onChange={handleAssinaturaFileChange}
                                    className="hidden"
                                    key="assinatura-input" // Força re-render quando necessário
                                />
                                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-emerald-500 transition-colors">
                                    {assinaturaFile ? (
                                        <div className="space-y-2">
                                            <FileText className="h-8 w-8 text-emerald-600 mx-auto" />
                                            <p className="text-sm font-medium text-gray-900">
                                                {assinaturaFile.name}
                                            </p>
                                            <p className="text-xs text-gray-500">
                                                {(assinaturaFile.size / 1024 / 1024).toFixed(2)} MB
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="space-y-2">
                                            <FileText className="h-8 w-8 text-gray-400 mx-auto" />
                                            <p className="text-sm text-gray-600">
                                                Clique para selecionar um arquivo PDF
                                            </p>
                                            <p className="text-xs text-gray-500">
                                                Máximo 5MB
                                            </p>
                                        </div>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => assinaturaInputRef.current?.click()}
                                        className="mt-4 px-4 py-2 text-sm font-medium text-emerald-600 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-colors"
                                    >
                                        {assinaturaFile ? 'Trocar Arquivo' : 'Selecionar Arquivo'}
                                    </button>
                                </div>
                            </div>

                            <div className="flex justify-end gap-3 pt-4">
                                <button
                                    type="button"
                                    onClick={cancelEditAssinatura}
                                    className="px-6 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors font-medium"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="button"
                                    onClick={handleSaveAssinatura}
                                    disabled={saving || !assinaturaFile}
                                    className="px-6 py-2 text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {saving ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                            Salvando...
                                        </>
                                    ) : (
                                        'Salvar Assinatura'
                                    )}
                                </button>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Card de Informações Pessoais */}
            <Card>
                <CardContent className="p-6 sm:p-8">
                    <div className="flex items-center justify-between mb-8">
                        <h2 className="text-2xl font-bold text-gray-800">
                            Informações Pessoais
                        </h2>
                        {!isEditingPersonal ? (
                            <button
                                onClick={() => setIsEditingPersonal(true)}
                                className="px-4 py-2 text-sm font-medium text-emerald-600 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-colors"
                            >
                                Editar
                            </button>
                        ) : (
                            <button
                                onClick={cancelEditPersonal}
                                className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                Cancelar
                            </button>
                        )}
                    </div>

                    {!isEditingPersonal ? (
                        <div className="space-y-6">
                            <div className="pb-2">
                                <p className="text-sm text-gray-500 mb-2">Nome Completo</p>
                                <p className="text-lg font-semibold text-gray-800">
                                    {profileData.nome_completo || 'Não informado'}
                                </p>
                            </div>

                            <div className="pb-2">
                                <p className="text-sm text-gray-500 mb-2">E-mail</p>
                                <p className="text-lg font-semibold text-gray-800">
                                    {profileData.email || 'Não informado'}
                                </p>
                            </div>

                            <div className="pb-2">
                                <p className="text-sm text-gray-500 mb-2">Telefone</p>
                                <p className="text-lg font-semibold text-gray-800">
                                    {profileData.telefone || 'Não informado'}
                                </p>
                            </div>
                        </div>
                    ) : (
                        <form onSubmit={handleSavePersonal} className="space-y-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Nome Completo *
                                </label>
                                <input
                                    type="text"
                                    value={nomeCompleto}
                                    onChange={(e) => setNomeCompleto(e.target.value)}
                                    required
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    E-mail *
                                </label>
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Telefone
                                </label>
                                <input
                                    type="tel"
                                    value={telefone}
                                    onChange={(e) => setTelefone(e.target.value)}
                                    placeholder="(00) 00000-0000"
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                                />
                            </div>

                            <div className="flex justify-end gap-3 pt-6">
                                <button
                                    type="button"
                                    onClick={cancelEditPersonal}
                                    className="px-6 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors font-medium"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    disabled={saving}
                                    className="px-6 py-2 text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {saving ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                            Salvando...
                                        </>
                                    ) : (
                                        'Salvar'
                                    )}
                                </button>
                            </div>
                        </form>
                    )}
                </CardContent>
            </Card>

            {/* Card de Alteração de Senha */}
            <Card>
                <CardContent className="p-6 sm:p-8">
                    <div className="flex items-center justify-between mb-8">
                        <h2 className="text-2xl font-bold text-gray-800">
                            Segurança
                        </h2>
                        {!isEditingPassword ? (
                            <button
                                onClick={() => setIsEditingPassword(true)}
                                className="px-4 py-2 text-sm font-medium text-emerald-600 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-colors"
                            >
                                Alterar Senha
                            </button>
                        ) : (
                            <button
                                onClick={cancelEditPassword}
                                className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                Cancelar
                            </button>
                        )}
                    </div>

                    {!isEditingPassword ? (
                        <div className="pb-2">
                            <p className="text-sm text-gray-500 mb-2">Senha</p>
                            <p className="text-lg font-semibold text-gray-800">
                                ••••••••••
                            </p>
                        </div>
                    ) : (
                        <form onSubmit={handleSavePassword} className="space-y-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Senha Atual *
                                </label>
                                <input
                                    type="password"
                                    value={senhaAtual}
                                    onChange={(e) => setSenhaAtual(e.target.value)}
                                    required
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Nova Senha * (mínimo 8 caracteres)
                                </label>
                                <input
                                    type="password"
                                    value={novaSenha}
                                    onChange={(e) => setNovaSenha(e.target.value)}
                                    required
                                    minLength={8}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Confirmar Nova Senha *
                                </label>
                                <input
                                    type="password"
                                    value={confirmarSenha}
                                    onChange={(e) => setConfirmarSenha(e.target.value)}
                                    required
                                    minLength={8}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                                />
                            </div>

                            <div className="flex justify-end gap-3 pt-6">
                                <button
                                    type="button"
                                    onClick={cancelEditPassword}
                                    className="px-6 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors font-medium"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    disabled={saving}
                                    className="px-6 py-2 text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {saving ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                            Salvando...
                                        </>
                                    ) : (
                                        'Alterar Senha'
                                    )}
                                </button>
                            </div>
                        </form>
                    )}
                </CardContent>
            </Card>

            {/* Card de Vínculos Pendentes */}
            {vinculosPendentes.length > 0 && (
                <Card>
                    <CardContent className="p-6 sm:p-8">
                        <div className="flex items-center gap-2 mb-6">
                            <AlertCircle className="w-6 h-6 text-amber-500" />
                            <h2 className="text-2xl font-bold text-gray-800">
                                Vínculos Pendentes
                            </h2>
                        </div>

                        <div className="space-y-4">
                            {vinculosPendentes.map((vinculo) => (
                                <div
                                    key={vinculo.organizacao_id}
                                    className="bg-amber-50 border border-amber-200 rounded-lg p-6"
                                >
                                    <div className="flex items-start justify-between mb-4">
                                        <div className="flex-1">
                                            <h3 className="text-lg font-semibold text-gray-800 mb-2">
                                                {vinculo.organizacao_nome || `Organização #${vinculo.organizacao_id}`}
                                            </h3>
                                            <div className="space-y-1 text-sm text-gray-600">
                                                <p>
                                                    <strong>Função:</strong> {getRoleLabel(vinculo.role)}
                                                </p>
                                                {vinculo.vinculado_por_nome && (
                                                    <p>
                                                        <strong>Convidado por:</strong> {vinculo.vinculado_por_nome}
                                                    </p>
                                                )}
                                                {vinculo.data_vinculacao && (
                                                    <p>
                                                        <strong>Data do convite:</strong>{' '}
                                                        {new Date(vinculo.data_vinculacao).toLocaleDateString('pt-BR')}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex gap-3">
                                        <button
                                            onClick={() => handleAceitarVinculo(vinculo.organizacao_id)}
                                            disabled={processandoVinculo === vinculo.organizacao_id}
                                            className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                                        >
                                            {processandoVinculo === vinculo.organizacao_id ? (
                                                <>
                                                    <span className="animate-spin">⏳</span>
                                                    Processando...
                                                </>
                                            ) : (
                                                <>
                                                    <CheckCircle2 className="w-4 h-4" />
                                                    Aceitar
                                                </>
                                            )}
                                        </button>
                                        <button
                                            onClick={() => handleRejeitarVinculo(vinculo.organizacao_id)}
                                            disabled={processandoVinculo === vinculo.organizacao_id}
                                            className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                                        >
                                            {processandoVinculo === vinculo.organizacao_id ? (
                                                <>
                                                    <span className="animate-spin">⏳</span>
                                                    Processando...
                                                </>
                                            ) : (
                                                <>
                                                    <XCircle className="w-4 h-4" />
                                                    Rejeitar
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Card de Criar Organização (apenas se não tiver organização) */}
            {(!profileData.organizacao_id || profileData.status === 'pendente_vinculacao') && (
                <Card>
                    <CardContent className="p-6 sm:p-8">
                        <div className="flex items-center justify-between mb-8">
                            <h2 className="text-2xl font-bold text-gray-800">
                                Criar Minha Organização
                            </h2>
                            {!isCreatingOrganization && (
                                <button
                                    onClick={() => setIsCreatingOrganization(true)}
                                    className="px-4 py-2 text-sm font-medium text-emerald-600 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-colors"
                                >
                                    Criar Organização
                                </button>
                            )}
                        </div>

                        {!isCreatingOrganization ? (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                                <p className="text-blue-800 mb-3">
                                    <strong>Você ainda não está vinculado a uma organização.</strong>
                                </p>
                                <p className="text-blue-700 text-sm leading-relaxed">
                                    Crie sua própria organização e se torne o proprietário. Você poderá gerenciar usuários, pacientes e todas as funcionalidades do sistema.
                                </p>
                            </div>
                        ) : (
                            <form onSubmit={handleCreateOrganization} className="space-y-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Nome da Organização *
                                    </label>
                                    <input
                                        type="text"
                                        value={nomeOrganizacao}
                                        onChange={(e) => setNomeOrganizacao(e.target.value)}
                                        required
                                        placeholder="Ex: Home Care Saúde"
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        CNPJ * (apenas números)
                                    </label>
                                    <input
                                        type="text"
                                        value={cnpj}
                                        onChange={(e) => setCnpj(e.target.value.replace(/\D/g, ''))}
                                        required
                                        placeholder="00000000000000"
                                        maxLength={14}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                                    />
                                    <p className="text-xs text-gray-500 mt-2">Digite apenas os 14 dígitos do CNPJ</p>
                                </div>

                                <div className="flex justify-end gap-3 pt-6">
                                    <button
                                        type="button"
                                        onClick={cancelCreateOrganization}
                                        className="px-6 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors font-medium"
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={saving}
                                        className="px-6 py-2 text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {saving ? (
                                            <>
                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                                Criando...
                                            </>
                                        ) : (
                                            'Criar Organização'
                                        )}
                                    </button>
                                </div>
                            </form>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Card de Informações da Conta */}
            <Card>
                <CardContent className="p-6 sm:p-8">
                    <h2 className="text-2xl font-bold text-gray-800 mb-8">
                        Informações da Conta
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                        <div className="pb-2">
                            <p className="text-sm text-gray-500 mb-2">Função</p>
                            <p className="text-lg font-semibold text-gray-800">
                                {getRoleLabel(profileData.role)}
                            </p>
                        </div>

                        <div className="pb-2">
                            <p className="text-sm text-gray-500 mb-2">Status</p>
                            <p className="text-lg font-semibold text-gray-800">
                                {getStatusLabel(profileData.status)}
                            </p>
                        </div>

                        <div className="pb-2">
                            <p className="text-sm text-gray-500 mb-2">Data de Cadastro</p>
                            <p className="text-lg font-semibold text-gray-800">
                                {formatDate(profileData.data_cadastro)}
                            </p>
                        </div>

                        {profileData.data_vinculacao && (
                            <div className="pb-2">
                                <p className="text-sm text-gray-500 mb-2">Data de Vinculação</p>
                                <p className="text-lg font-semibold text-gray-800">
                                    {formatDate(profileData.data_vinculacao)}
                                </p>
                            </div>
                        )}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default ProfilePage;
